<template>
  <QDialog v-model="isOpen" @hide="afterLeave">
    <QCard>
      <div class="modal">
        <div class="modal__header">
          <h2 class="modal__title">Создание номенклатуры</h2>
          <iconClose class="link gray-btn" @click="close()" />
        </div>
        <div class="modal__field narrow">
          <label class="modal__label">Артикул*</label>
          <QInput outlined v-model="form.data.article" type="text"></QInput>
        </div>

        <label class="modal__label">Габариты, см*</label>

        <div class="modal__size-wrapp narrow">
          <label class="">Ш:</label>
          <QInput
            outlined
            v-model="form.data.width"
            type="number"
            min="0"
            class="modal__size-input"
          ></QInput>
          <label class="">В:</label>
          <QInput
            outlined
            v-model="form.data.height"
            type="number"
            min="0"
            class="modal__size-input"
          ></QInput>
          <label class="">Г:</label>
          <QInput
            outlined
            v-model="form.data.depth"
            type="number"
            min="0"
            class="modal__size-input"
          ></QInput>
        </div>

        <div class="modal__field narrow">
          <label class="modal__label">Штрихкод</label>
          <QInput outlined v-model="form.data.barcode" type="text"></QInput>
        </div>

        <div class="modal__field narrow">
          <label class="modal__label">Вес, кг*</label>
          <QInput
            outlined
            v-model="form.data.weight"
            type="number"
            min="0"
          ></QInput>
        </div>

        <div class="modal__field narrow">
          <label class="modal__label">Количество слоёв</label>
          <QInput
            outlined
            v-model="form.data.max_layer_height"
            type="number"
            min="0"
          ></QInput>
        </div>

        <div class="modal__field narrow">
          <label class="modal__label">Количество в слое</label>
          <QInput
            outlined
            v-model="form.data.max_in_layer"
            type="number"
            min="0"
          ></QInput>
        </div>

        <div class="modal__field narrow modal__field--fragility">
          <label class="modal__label">Хрупкость*</label>
          <div class="modal__fragility--text">
            <span class="gray-btn">Менее хрупкий</span>
            <span class="gray-btn">Более хрупкий</span>
          </div>
          <q-slider
            v-model="selectedOption"
            :marker-labels="
              fragilityOptions.map((option) => option.label.toString())
            "
            :min="0"
            :max="5"
          />
        </div>

        <div class="modal__field">
          <div class="column">
            <button
              class="btn btn-primary"
              @click="submit()"
              :disabled="isSubmitDisabled || state.preloader"
            >
              Сохранить
            </button>
          </div>
        </div>
      </div>
    </QCard>
  </QDialog>
</template>

<script lang="ts">
interface IState {
  preloader: boolean;
  errors: errorsObjectType;
}

interface IInitData {
  article: string;
  depth: number | null;
  width: number | null;
  height: number | null;
  weight: number | null;
  barcode: string;
  fragility: number | null;
  max_layer_height: number | null;
  max_in_layer: number | null;
}

interface IForm {
  data: IInitData;
}

const initData = () => ({
  article: '',
  depth: null,
  width: null,
  height: null,
  weight: null,
  barcode: '',
  fragility: null,
  max_layer_height: null,
  max_in_layer: null,
});

import { defineComponent, ref, reactive, computed } from 'vue';
import iconClose from '@/components/icons/iconClose.vue';
import { QDialog, QCard, QInput, QCheckbox, QSelect, QSlider } from 'quasar';
import iconCopy from '@/components/icons/iconCopy.vue';
import iconDelete from '@/components/icons/iconDelete.vue';
import * as dictionaryService from '@/api/dictionaryService';
import { errorsObjectType } from '@/types/common';
import { showError } from '@/utils/errorsInterceptor';
import { errorsConst } from '@/constants/common';
import type { IOption } from '@/types/common';

export default defineComponent({
  name: 'CreateNomenclatureModal',
  components: {
    iconClose,
    QDialog,
    QCard,
    QInput,
    QCheckbox,
    QSelect,
    iconCopy,
    QSlider,
    iconDelete,
  },
  emits: ['close', 'reload'],
  props: {},

  setup(props, { emit }) {
    const isOpen = ref(false);
    const state: IState = reactive({
      preloader: false,
      errors: errorsConst,
    });

    const form: IForm = reactive({
      data: initData(),
    });

    const fragilityOptions = ref<IOption[]>([
      { label: '0', value: 0 },
      { label: '1', value: 1 },
      { label: '2', value: 2 },
      { label: '3', value: 3 },
      { label: '4', value: 4 },
      { label: '5', value: 5 },
    ]);

    const selectedOption = ref<number | null>(0);

    const submit = async () => {
      const params = {
        article: form.data.article,
        depth: Number(form.data.depth),
        width: Number(form.data.width),
        height: Number(form.data.height),
        weight: Number(form.data.weight),
        max_layer_height:
          form.data.max_layer_height == 0
            ? null
            : Number(form.data.max_layer_height) || null,
        max_in_layer:
          form.data.max_in_layer == 0
            ? null
            : Number(form.data.max_in_layer) || null,

        barcode: form.data.barcode,
        fragility: selectedOption.value ?? null,
      };
      try {
        state.preloader = true;
        const response = await dictionaryService.createNomenclature(params);
        state.preloader = false;
        if (response) {
          emit('reload');
          close();
        }
      } catch (error: any) {
        state.preloader = false;
        showError(error);
      }
    };

    const isSubmitDisabled = computed((): boolean => {
      return (
        !form.data.article ||
        !form.data.width ||
        !form.data.height ||
        !form.data.depth ||
        !form.data.weight
      );
    });

    const close = () => {
      form.data = initData();
      selectedOption.value = 0;
      isOpen.value = false;
    };

    const afterLeave = () => {
      form.data = initData();
      selectedOption.value = 0;
    };

    const reset = (): void => {
      form.data = initData();
      selectedOption.value = 0;
    };

    const showModal = (nomenclature: IInitData | null = null) => {
      isOpen.value = true;

      if (nomenclature) {
        form.data = { ...nomenclature };
        selectedOption.value =
          fragilityOptions.value.find(
            (option) => option.value === nomenclature.fragility
          )?.value ?? null;
      }
    };

    return {
      isOpen,
      close,
      afterLeave,
      reset,
      form,
      state,
      submit,
      isSubmitDisabled,
      showModal,
      fragilityOptions,
      selectedOption,
    };
  },
});
</script>

<style lang="scss" scoped>
.modal {
  max-width: 480px;
  padding: 48px;
  display: flex;
  flex-direction: column;
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__title {
    font-weight: 700;
    font-size: 23px;
    line-height: 28px;
    letter-spacing: 1px;
    color: var(--typography-blue);
  }
  &__label {
    margin-top: 20px;
    margin-bottom: 7px;
    font-size: 14px;
    font-weight: 600;
    line-height: 17px;
    color: var(--typography-1);
  }

  &__field {
    position: relative;
    margin-top: 24px;
    min-width: 384px;
  }
  &__field-error {
    position: absolute;
    bottom: -19px;
    font-size: 12px;
    color: var(--typography-error);
  }
  &__use-default {
    display: flex;
    justify-content: space-between;
    margin-left: -8px;
    margin-top: 12px;
    align-items: center;
  }
  &__no-accaunt {
    margin: 16px auto 0px auto;
  }
  &__size-input {
    max-width: 100px;
    width: 100%;
  }

  &__size-wrapp {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__footer-wrapp {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  &__footer--copy {
    display: flex;
    justify-content: space-between;
    align-items: center;
    column-gap: 8px;
    &:hover {
      color: var(--primary-hover);
    }
  }
  &__footer--delete {
    display: flex;
    justify-content: space-between;
    align-items: center;
    column-gap: 8px;
  }

  &__fragility--text {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-top: 4px;
  }
}
</style>
